import { OrganType } from '@/api/apis/case';
import { DayOfWeek } from '@/types/date.types';
import { ApiError } from '@/api/types';
import {
  AutoSchedulerResultType,
  CCResourceType,
  CCShiftType,
  CSShiftType,
  OMSShiftType,
  CSShiftTimeGroup,
  PlanningProcessStatus,
  ScheduleFillingState,
  ScheduleHrSystemConflictType,
  ShiftFixationType,
  ShiftStatus,
  SShiftType,
} from '@/api/apis/resources/schema.d';

export {
  AutoSchedulerResultType,
  CCShiftType,
  CCResourceType,
  CSShiftType,
  OMSShiftType,
  CSHolidayDutyGroup,
  CSShiftTimeGroup,
  SShiftType,
  ShiftStatus,
  ShiftFixationType,
  PlanningProcessStatus,
  ScheduleFillingState,
  ScheduleHrSystemConflictType,
} from '@/api/apis/resources/schema.d';

export enum CriticalRuleViolationType {
  EmptySchedule = 'EmptySchedule',
  CriticalResourceRuleViolation = 'CriticalResourceRuleViolation',
  CriticalDailyRuleViolation = 'CriticalDailyRuleViolation',
  CriticalDailySlotRuleViolation = 'CriticalDailySlotRuleViolation',
}

export enum CriticalResourceViolatedRuleName {
  ResourceHasShiftEveryDay = 'ResourceHasShiftEveryDay',
}

export enum CriticalDailyViolatedRuleName {
  OneOnDutyPerDay = 'OneOnDutyPerDay',
}

export enum CriticalDailySlotViolatedRuleName {
  NoOnCallForOrgan = 'NoOnCallForOrgan',
}

export enum ByFlagRuleViolationName {
  IsManager = 'IsManager',
}

export type ShiftType = CSShiftType | SShiftType | CCShiftType | OMSShiftType;

export type BaseShift<T extends string> = {
  from?: string;
  to?: string;
  scheduleId: string;
  day: string;
  resourceId: string;
  shiftStatus: ShiftStatus;
  isEditable: boolean;
  shiftType: T;
  isPreSchedule?: boolean;
  fixationType?: ShiftFixationType;
  isHrShift?: boolean;
};

export type CSShift = BaseShift<CSShiftType> & {
  timeGroup?: CSShiftTimeGroup | null;
  isTravel: boolean;
  isExtra: boolean;
  organs?: OrganType[];
  regionId?: string;
  hubId?: string;
};

export type Unavailability = {
  from: string;
  to: string;
};

export type SShift = BaseShift<SShiftType> & {
  isTravel: boolean;
  regionId?: string;
  organs?: OrganType[];
  unavailabilities?: Unavailability[];
};

export type CCShift = BaseShift<CCShiftType> & {
  isManager: boolean | null;
};

export type OMSShiftSlot = {
  slotIndex: number; // Index of the time slot in the schedule day (1-based).
  ofSlotsPerDay: number; // Total number of slots per day in the schedule.
  shiftType: OMSShiftType; // Type of Organ Management Specialist shift.
  organs: OrganType[] | null; // Organs for which the shift is assigned.
  isVoluntary: boolean; // This shift exceeds the monthly norm but employee agrees to take additional shifts.
  // Optional only in FE for optimistic updates simplicity
  from?: string; // The start timestamp of the shift slot.
  to?: string; // The end timestamp of the shift slot.
};

export type OMSShift = BaseShift<never> & {
  slot1: OMSShiftSlot | null;
  slot2: OMSShiftSlot | null;
};

export type BaseResource = {
  id: string;
  firstName: string;
  lastName: string;
  profilePhoto: string;
};

export type CSResource = BaseResource & {
  organs: OrganType[];
  phoneSupportOrgans: OrganType[] | null;
  proctorOrgans: OrganType[];
  shadowOrgans: OrganType[];
  regionId: string;
  hubId: string;
  canPhoneSupport: boolean;
  shiftTimeGroup?: CSShiftTimeGroup;
};

export type SResource = BaseResource & {
  organs: OrganType[];
  regionId: string;
  hubId: string;
  timezone?: string;
};

export type CCResource = BaseResource & {
  type: CCResourceType;
};

export type OMSResource = BaseResource & {
  organs: OrganType[] | null;
};

export type Hub = {
  id: string;
  regionId: string;
  name: string;
};

export type Region = {
  id: string;
  name: string;
  hubs: Hub[];
};

export type SRegion = Region & {
  organs: OrganType[];
};

export type ResourceScheduleSegment<R, S> = {
  resource: R;
  days: Record<string, S>;
};

export type ResourcesWithShifts<R extends BaseResource, S extends BaseShift<string>> = Record<
  string,
  ResourceScheduleSegment<R, S>
>;

export type CSResourceScheduleSegment = ResourceScheduleSegment<CSResource, CSShift>;

export type SResourceScheduleSegment = ResourceScheduleSegment<SResource, SShift>;

export type CCResourceScheduleSegment = ResourceScheduleSegment<CCResource, CCShift>;

export type OMSResourceScheduleSegment = ResourceScheduleSegment<OMSResource, OMSShift>;

export type CSResourcesWithShifts = Record<string, CSResourceScheduleSegment>;

export type SResourcesWithShifts = Record<string, SResourceScheduleSegment>;

export type CCResourcesWithShifts = Record<string, CCResourceScheduleSegment>;

export type OMSResourcesWithShifts = Record<string, OMSResourceScheduleSegment>;

type ShiftsResponseBody<RS> = {
  from: string;
  to: string;
  resourcesShifts: RS;
};

export type CSShiftsResponseBody = ShiftsResponseBody<CSResourcesWithShifts>;

export type SShiftsResponseBody = ShiftsResponseBody<SResourcesWithShifts>;

export type CCShiftsResponseBody = ShiftsResponseBody<CCResourcesWithShifts>;

export type OMSShiftsResponseBody = ShiftsResponseBody<OMSResourcesWithShifts>;

export type SetCSShiftTypeRequestBody = CSShift;

export type SetSShiftTypeRequestBody = SShift;

export type SetCCShiftTypeRequestBody = CCShift;

export type SetOMSShiftTypeRequestBody = SetOMSShiftData & {
  scheduleId: string;
  resourceId: string;
  day: string;
  shiftStatus: ShiftStatus;
  isEditable: boolean;
  fixationType?: ShiftFixationType;
  isPreSchedule?: boolean;
};

export type SetMyCCShiftTypeRequestBody = Omit<CCShift, 'resourceId'> & {
  required: boolean;
};

export type ResetShiftTypeRequestBody = {
  scheduleId: string;
  resourceId: string;
  day: string;
};

export type ResetMyShiftTypeRequestBody = Omit<ResetShiftTypeRequestBody, 'resourceId'>;

export type SetFixationTypeRequestBody = {
  scheduleId: string;
  resourceId: string;
  day: string;
  fixationType: ShiftFixationType;
};

export type DateRangeParams = {
  from: Date;
  to: Date;
};

export type AutoSchedulerInfo = {
  hasSolution: boolean;
  resultType: AutoSchedulerResultType;
  spendTime: string;
  errorMessage: string | null;
  isFinal: boolean;
  foundSolutions: number;
  softRulesViolations: number;
};

export type PlanningProcess = {
  id: string;
  startDayOfMonth: string;
  status: PlanningProcessStatus;
  scheduleId?: string;
  jobId?: string;
  jobCancellationRequested: boolean;
  autoSchedulerInfo: AutoSchedulerInfo | null;
  lastErrors: ApiError[] | null;
  hrSystemError: ApiError | null;
};

export type MonthSchedule = {
  startDayOfMonth: string;
  endDayOfMonth: string;
  hasSchedule: boolean;
  hasPreSchedule: boolean;
  hasPlanningProcess: boolean;
  scheduleId: string | null;
  preScheduleId: string | null;
  planningProcessId: string | null;
  inPlanningScheduleId: string | null;
  planningProcessStatus: PlanningProcessStatus | null;
  scheduleHrSystemLastSyncDate: string | null;
  preScheduleHrSystemLastSyncDate: string | null;
  inPlanningScheduleHrSystemLastSyncDate: string | null;
};

export type CCShiftSelectPayload = {
  shiftType: CCShiftType;
  isManager: boolean | null;
};

export type SetCCShiftData = CCShiftSelectPayload;

export type CSShiftSelectPayload = {
  shiftType: CSShiftType;
  timeGroup?: CSShiftTimeGroup;
  isExtra: boolean;
  organs?: OrganType[];
  fixationType?: ShiftFixationType;
};

export type SetCSShiftData = CSShiftSelectPayload & {
  regionId?: string;
  hubId?: string;
  isTravel: boolean;
};

export type SShiftSelectPayload = {
  shiftType: SShiftType;
  unavailabilities?: Unavailability[];
};

export type SetSShiftData = SShiftSelectPayload & {
  organs?: OrganType[];
  regionId?: string;
  isTravel: boolean;
};

export type OMSShiftSlotPayload = {
  shiftType: OMSShiftType;
  organs?: OrganType[] | null;
  isVoluntary?: boolean;
};

export type OMSShiftSelectPayload = {
  slotsPerDay: number;
  // null for resetting the slot, undefined for no change
  slot1?: OMSShiftSlotPayload | null;
  slot2?: OMSShiftSlotPayload | null;
};

export type SetOMSShiftData = {
  slotsPerDay: number;
  slot1?: OMSShiftSlot | null;
  slot2?: OMSShiftSlot | null;
};

/** SETTINGS RULES * */
export type LeadShiftPerMonthRules = {
  onCall: number;
  onCallOnWeekend: number;
  maxAtHub: number;
  maxPhoneSupport: number;
  onCallAndPhoneSupport: number;
};

export type SpecialistShiftPerMonthRules = {
  onCall: number;
  onCallOnWeekend: number;
  maxAtHub: number;
};

export type ShiftPerMonthRules = {
  lead: LeadShiftPerMonthRules;
  specialist: SpecialistShiftPerMonthRules;
};

export type OnCallInRowRules = {
  hardMin: number;
  recommendedMin: number;
  recommendedMax: number;
  hardMax: number;
};

export type NoCallInRowRules = {
  hardMin: number;
  recommendedMin: number;
  recommendedMax: number;
  hardMax: number;
};

export type CCTypeSpecificRules = {
  minOnCall: number;
  maxOnCall: number;
};

export type VacationDeductionRule = {
  each: number;
  deduction: number;
};

export type YearDeductionRules = {
  q1_3: VacationDeductionRule;
  q4: VacationDeductionRule;
};

export type CCCustomRule = {
  resourceId: string;
  status: CCShiftType;
  dayOfWeeks?: DayOfWeek[] | null;
};

/**
 * Monthly family - `GET/POST /settings/rules`.
 * Carries the workload members that the period family replaces with the deduction table.
 */
export type CSResourceRulesSettings = {
  shiftPerPeriods: ShiftPerMonthRules;
  minimumWeekends: number;
  noCallDaysPerPeriod: number;
  onCallInRow: OnCallInRowRules;
  vacationDeduction: YearDeductionRules;
  holidayNoCallBonus: number;
};

/**
 * Period family - `GET/POST /settings/period/rules`.
 * Deliberately not a projection of the monthly contract:
 * `shiftPerPeriods`, `minimumWeekends`, `noCallDaysPerPeriod` and `vacationDeduction`
 * are all superseded by the absence-deduction table.
 */
export type CSPeriodResourceRulesSettings = {
  onCallInRow: OnCallInRowRules;
  holidayNoCallBonus: number;
};

export type CSDeductionShiftRule = {
  onCall: number;
  weekendOnCall: number;
  atHub: number;
  officeDay: number;
  noCall: number;
  weekendOff: number;
};

export type CSDeductionShiftRulesSettings = {
  byAbsenceDays: Record<number, CSDeductionShiftRule>; // keys must be exactly 0..28.
};

export type SResourceRulesSettings = {
  minNoCallDaysPerPeriod: number;
  maxNoCallDaysPerPeriod: number;
  noCallInRow: NoCallInRowRules;
};

export type CCResourceRulesSettings = {
  tcOneTwoRules: CCTypeSpecificRules;
  tcThreeRules: CCTypeSpecificRules;
  weekendCount: number;
  deductionRule: VacationDeductionRule;
  customRules?: CCCustomRule[] | null;
};

/** SETTINGS INPUTS * */
export type ComboMinRule = {
  strict: number;
  flex: number;
};

export type OrganTypeComboMinRules = Record<OrganType, ComboMinRule>;

export type TimeGroupComboMinRule = Record<CSShiftTimeGroup, ComboMinRule>;

export type OrganTypeTimeGroupComboMinRules = Record<OrganType, TimeGroupComboMinRule>;

/**
 * Monthly family - `GET/POST /settings/inputs`.
 * Coverage is keyed per organ, and `atHubDays` is part of this payload rather than its own endpoint.
 */
export type CSRequirementsInputsSettings = {
  exactlyDailyPhoneSupport: Record<OrganType, number>;
  perRegions: Record<string, OrganTypeComboMinRules>;
  perHubs: Record<string, OrganTypeComboMinRules>;
  atHubDays: Record<string, string[]>;
};

/**
 * Period family - `GET/POST /settings/period/time-group-inputs`.
 * Coverage gains the Shift Time Group dimension, and `atHubDays` moves to its own endpoint.
 * `exactlyDailyPhoneSupport` is optional and obsolete here - PhoneSupport is retired on the period path -
 * so it is not rendered by the period UI.
 */
export type CSTimeGroupRequirementsInputsSettings = {
  perRegions: Record<string, OrganTypeTimeGroupComboMinRules>;
  perHubs: Record<string, OrganTypeTimeGroupComboMinRules>;
  exactlyDailyPhoneSupport?: Record<OrganType, number>;
};

/** Period family - `GET/POST /settings/period/at-hub-weekly`. */
export type CSAtHubWeeklyRequirementsInputsSettings = {
  atHubDays: Record<string, string[]>;
};

export type SRequirementsInputsSettings = {
  perRegions: Record<string, OrganTypeComboMinRules>;
};

export type CCRequirementsInputsSettings = {
  onCallPerDay: number;
};

export type OMSRequirementsInputsSettings = {
  minForFirstSlotPerOrgan: OrganTypeComboMinRules;
  minForSecondSlotPerOrgan: OrganTypeComboMinRules;
};

export type OMSResourceRulesSettings = {
  maxOnCallShiftsSlotsPerPeriod: number;
  minOnCallShiftsSlotsPerPeriod: number;
  maxAdminDaySlotsPerPeriod: number;
  minAdminDaySlotsPerPeriod: number;
};

/** VIOLATIONS * */
export type RangeRule = {
  inclusiveFrom: number | null;
  inclusiveTo: number | null;
};

export type RangeRuleViolation = {
  actualValue: number;
  violatedRule: RangeRule;
};

export type ComboRangeRuleViolation = {
  actualValue: number;
  violatedRule: {
    hard: RangeRule;
    soft: RangeRule;
  };
  /** `actualValue` is outside `hard`. `null`/absent in legacy results - derive from the ranges. */
  hardViolated?: boolean | null;
  /** `actualValue` is outside `soft`. `null`/absent in legacy results - derive from the ranges. */
  softViolated?: boolean | null;
};

export type ShiftInRowRuleViolation = {
  violatedRule: {
    hard: RangeRule;
    soft: RangeRule;
  };
  violations: {
    actualLengthInRow: number;
    startDate: string;
    endDate: string;
    /** The run is outside `violatedRule.hard`. `null`/absent in legacy results. */
    hardViolated?: boolean | null;
    /** The run is outside `violatedRule.soft`. `null`/absent in legacy results. */
    softViolated?: boolean | null;
  }[];
};

export type ShiftTypeNeighborRule<T extends ShiftType> = {
  daysBefore: number;
  daysAfter: number;
  beforeShiftTypes: T[];
  afterShiftTypes: T[];
};

export type ShiftTypeShiftNeighborRuleViolation<T extends ShiftType> = {
  targetDate: string;
  actualBefore: T[];
  actualAfter: T[];
};

export type ShiftTypeShiftNeighborRuleViolations<T extends ShiftType> = {
  violatedRule: ShiftTypeNeighborRule<T>;
  violations: ShiftTypeShiftNeighborRuleViolation<T>[];
};

export type ShiftTypeShiftAllowedDayOfWeekRuleViolation = {
  targetDate: string;
  actual: DayOfWeek;
};

export type ShiftTypeShiftAllowedDayOfWeekRuleViolations = {
  actualRule: DayOfWeek[];
  violations: ShiftTypeShiftAllowedDayOfWeekRuleViolation[];
};

export type ServiceShiftTimeGroupRulesViolations = {
  global: ComboRangeRuleViolation | null;
  byOrganType: Record<OrganType, ComboRangeRuleViolation>;
  byFlag: Record<ByFlagRuleViolationName, ComboRangeRuleViolation>;
  /** Daily: that day's weekday. Weekly: the weekdays whose shifts break the rule (all 7 when any day is allowed). */
  globalViolatedDaysOfWeek?: DayOfWeek[];
  byOrganTypeViolatedDaysOfWeek?: Partial<Record<OrganType, DayOfWeek[]>>;
};

export type ServiceShiftTypeRulesViolations = ServiceShiftTimeGroupRulesViolations & {
  /** CS period path only, under `OnCall` of hubs/regions: coverage counted per Shift Time Group. */
  byShiftTimeGroup?: Record<CSShiftTimeGroup, ServiceShiftTimeGroupRulesViolations>;
};

export type ShiftTypeServiceRulesViolations<T extends ShiftType> = Record<T, ServiceShiftTypeRulesViolations>;

export type ShiftTypeResourceRulesViolations<T extends ShiftType> = {
  periodShiftCountByTypeViolations?: Record<T, RangeRuleViolation>;
  periodShiftCountOnWeekEndViolations?: Record<T, RangeRuleViolation>;
  /** On Call + Clinical Lead (Phone Support) total. CS period path also counts prescheduled Travel Days. */
  periodShiftCountOnCallPhoneSupportViolation?: RangeRuleViolation | null;
  /** Off Days: No Call + Weekend Off. */
  periodShiftCountNoCallWeekEndViolation?: RangeRuleViolation | null;
  /** CS period path only: Flex Days cap (At Hub + Office Day (Admin Day) + prescheduled Travel Day). */
  periodShiftCountFlexDaysViolation?: RangeRuleViolation | null;
  /** Vacation + Sick + Unavailable. Always `null` for now: no rule is registered on BE. */
  periodShiftCountSpecialRestViolation?: RangeRuleViolation | null;
  shiftTypeInRowViolations?: Record<T, ShiftInRowRuleViolation>;
  shiftNeighborViolations?: Record<T, ShiftTypeShiftNeighborRuleViolations<T>>;
  shiftAllowedDayOfWeekViolations?: Record<T, ShiftTypeShiftAllowedDayOfWeekRuleViolations>;
};

export type ShiftTypeServicesRulesViolations<T extends ShiftType> = {
  countryRulesViolations: ShiftTypeServiceRulesViolations<T>;
  regionsRulesViolations: Record<string, ShiftTypeServiceRulesViolations<T>>;
  hubsRulesViolations: Record<string, ShiftTypeServiceRulesViolations<T>>;
};

export type ScheduleSize = {
  currentShiftCount: number;
  resourceCount: number;
  daysCount: number;
  expectedShiftCount: number;
  missedShiftCount: number;
  fillingState: ScheduleFillingState;
  partialFilledResourceIds: string[];
};

export type CriticalResourceRuleViolationData = {
  CriticalViolatedRuleName: CriticalResourceViolatedRuleName;
  ResourcesWithViolation: string[];
};

export type CriticalDailyRuleViolationData = {
  CriticalViolatedRuleName: CriticalDailyViolatedRuleName;
  DaysWithViolation: string[];
};

export type CriticalDailySlotRuleViolationData = {
  CriticalViolatedRuleName: CriticalDailySlotViolatedRuleName;
  DaySlotsWithViolation: { Day: string; Slot: number }[];
};

export type ScheduleEmptyPublishBlocker = {
  type: CriticalRuleViolationType.EmptySchedule;
  message: string;
  data: NonNullable<unknown>;
};

export type ScheduleResourcePublishBlocker = {
  type: CriticalRuleViolationType.CriticalResourceRuleViolation;
  message: string;
  data: CriticalResourceRuleViolationData;
};

export type ScheduleDailyPublishBlocker = {
  type: CriticalRuleViolationType.CriticalDailyRuleViolation;
  message: string;
  data: CriticalDailyRuleViolationData;
};

export type ScheduleDailySlotPublishBlocker = {
  type: CriticalRuleViolationType.CriticalDailySlotRuleViolation;
  message: string;
  data: CriticalDailySlotRuleViolationData;
};

export type SchedulePublishBlocker =
  | ScheduleEmptyPublishBlocker
  | ScheduleResourcePublishBlocker
  | ScheduleDailyPublishBlocker
  | ScheduleDailySlotPublishBlocker;

export type SchedulePublishReadiness = {
  isReadyToPublish: boolean;
  blockers: SchedulePublishBlocker[] | null;
};

export type ShiftTypeScheduleValidationResult<T extends ShiftType> = {
  dailyRulesViolations: Record<string, ShiftTypeServicesRulesViolations<T>>;
  weeklyRulesViolations: Record<string, ShiftTypeServicesRulesViolations<T>>;
  resourceRulesViolations: Record<string, ShiftTypeResourceRulesViolations<T>>;
  hasRuleViolations: boolean;
  /** `null`/absent in legacy results. */
  hasHardRuleViolations?: boolean | null;
  /** `null`/absent in legacy results. */
  hasSoftRuleViolations?: boolean | null;
  isPreprocessed: boolean;
  scheduleSize: ScheduleSize;
  publishReadiness: SchedulePublishReadiness;
};

export type CSShiftTypeScheduleValidationResult = ShiftTypeScheduleValidationResult<CSShiftType>;

export type SShiftTypeScheduleValidationResult = ShiftTypeScheduleValidationResult<SShiftType>;

export type CCShiftTypeScheduleValidationResult = ShiftTypeScheduleValidationResult<CCShiftType>;

export type OMSShiftTypeScheduleValidationResult = ShiftTypeScheduleValidationResult<OMSShiftType>;

/** HR SYSTEM * */
export type ResourceShiftTypeHrSystemConflict<R extends BaseResource, T extends ShiftType> = {
  conflictId: string;
  scheduleId: string;
  type: ScheduleHrSystemConflictType;
  resourceId: string;
  resource: R;
  day: string;
  hrSystemShiftType: T;
  scheduleShiftType: T;
};

export type CCResourceCCShiftTypeHrSystemConflict = ResourceShiftTypeHrSystemConflict<CCResource, CCShiftType>;

export type CSResourceCSShiftTypeHrSystemConflict = ResourceShiftTypeHrSystemConflict<CSResource, CSShiftType>;

export type SResourceSShiftTypeHrSystemConflict = ResourceShiftTypeHrSystemConflict<SResource, SShiftType>;

export type OMSResourceOMSShiftTypeHrSystemConflict = ResourceShiftTypeHrSystemConflict<OMSResource, OMSShiftType>;

export type ResolveHrSystemConflict<D extends Record<string, unknown>> = {
  conflictId: string;
  setShiftData: D;
};

export type CCResolveHrSystemConflict = ResolveHrSystemConflict<SetCCShiftData>;

export type CSResolveHrSystemConflict = ResolveHrSystemConflict<SetCSShiftData>;

export type SResolveHrSystemConflict = ResolveHrSystemConflict<SetSShiftData>;

export type OMSResolveHrSystemConflict = ResolveHrSystemConflict<SetOMSShiftData>;
